import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCalendarCheck, faFileInvoiceDollar, faPaw, faUser } from '@fortawesome/pro-regular-svg-icons';
import moment from 'moment-timezone';
import { DataExport, DataExportResource, isDataExportExpired, isDataExportRunning } from 'model/dataExports';
import { StatusPillTone } from 'components/EmailMarketing/UI/StatusPill/StatusPill';

export interface DataExportResourceOption {
    id: DataExportResource;
    label: string;
    description: string;
    icon: IconDefinition;
}

export const DATA_EXPORT_RESOURCES: Array<DataExportResourceOption> = [
    {
        id: 'clients',
        label: 'Clients',
        description: 'Name, email, phone, notes and communication preferences.',
        icon: faUser
    },
    {
        id: 'pets',
        label: 'Pets',
        description: 'Type, breed, size, birthdate, allergies and grooming notes.',
        icon: faPaw
    },
    {
        id: 'appointments',
        label: 'Appointments',
        description: 'Date and time, client, pet, service, staff and status.',
        icon: faCalendarCheck
    },
    {
        id: 'invoices',
        label: 'Invoices',
        description: 'Invoice number, client, issue date, tips and void status.',
        icon: faFileInvoiceDollar
    }
];

export const getResourceOption = (resource: DataExportResource): DataExportResourceOption =>
    DATA_EXPORT_RESOURCES.find(option => option.id === resource) ?? DATA_EXPORT_RESOURCES[0];

export type DataExportDisplayStatus = 'ready' | 'preparing' | 'failed' | 'expired';

export const getDisplayStatus = (dataExport: DataExport): DataExportDisplayStatus => {
    if (isDataExportRunning(dataExport)) return 'preparing';
    if (dataExport.status === 'failed') return 'failed';
    if (isDataExportExpired(dataExport)) return 'expired';
    return 'ready';
};

export const DISPLAY_STATUS_PILL: Record<DataExportDisplayStatus, { label: string; tone: StatusPillTone }> = {
    ready: { label: 'Ready', tone: 'success' },
    preparing: { label: 'Preparing', tone: 'progress' },
    failed: { label: 'Failed', tone: 'error' },
    expired: { label: 'Expired', tone: 'neutral' }
};

export const formatDateRange = (startDate: string | null, endDate: string | null): string => {
    if (!startDate || !endDate) return 'All time';

    const from = moment(startDate, 'YYYY-MM-DD');
    const to = moment(endDate, 'YYYY-MM-DD');

    if (from.isSame(to, 'day')) return from.format('MMM D, YYYY');
    if (from.isSame(to, 'year')) return `${from.format('MMM D')} – ${to.format('MMM D, YYYY')}`;
    return `${from.format('MMM D, YYYY')} – ${to.format('MMM D, YYYY')}`;
};

export const formatRowCount = (rowCount: number | null): string | null => {
    if (rowCount === null) return null;
    return `${rowCount.toLocaleString('en-US')} ${rowCount === 1 ? 'row' : 'rows'}`;
};

// "Today, 10:42 AM", "Yesterday, 4:05 PM", "Oct 21, 9:12 AM"
export const formatRequestedAt = (createdAt: string): string => {
    const date = moment(createdAt);

    if (date.isSame(moment(), 'day')) return `Today, ${date.format('h:mm A')}`;
    if (date.isSame(moment().subtract(1, 'day'), 'day')) return `Yesterday, ${date.format('h:mm A')}`;
    if (date.isSame(moment(), 'year')) return date.format('MMM D, h:mm A');
    return date.format('MMM D, YYYY');
};

export const formatExpiresIn = (urlExpiresAt: string | null): string | null => {
    if (!urlExpiresAt) return null;

    const minutes = moment(urlExpiresAt).diff(moment(), 'minutes');
    if (minutes <= 0) return null;
    if (minutes < 60) return `Expires in ${minutes} min`;
    return `Expires in ${Math.floor(minutes / 60)}h`;
};

export const buildFileName = (dataExport: DataExport): string =>
    `${dataExport.resource}-${moment(dataExport.createdAt).format('YYYY-MM-DD')}.csv`;
